'use client';

import { useEffect, useMemo, useState } from 'react';

import { Align, Box, Button, ButtonVariant, Justify, Text, View } from '@bytel/trilogy-react';

import { getLocalDataIfNotExpired } from '@services/storage';

import { ProviderSummary } from '@app-types/eligibility';
import { FaiTabsEnum } from '@app-types/home-fai';

import { useMediaQuery } from '@hooks/use-media-query';

import {
    FAI_COMPARATOR_DESKTOP_LIMIT,
    FAI_COMPARATOR_MOBILE_LIMIT,
    FAI_COMPARATOR_SELECTION_BREAKPOINT,
    FaiComparatorSelectedOffer,
    FaiComparatorSelectionEventDetail,
    getStoredFaiComparatorSelection,
    sendComparatorButtonTracking,
} from '@helpers/fai-comparator';
import { paths } from '@helpers/path';

import { CUSTOM_EVENTS, ELIGIBILITY_STORAGE_KEY } from '@constants/provider';

interface StickyBarProps {
    activeTab: FaiTabsEnum;
}

export function StickyBar({ activeTab }: Readonly<StickyBarProps>) {
    const [buttonLabel, setButtonLabel] = useState('Tester mon éligibilité');
    const [eligibilitySentence, setEligibilitySentence] = useState('');
    const [providerSummary, setProviderSummary] = useState<ProviderSummary | null>(null);
    const [selectedOffers, setSelectedOffers] = useState<FaiComparatorSelectedOffer[]>([]);
    const maxComparatorSelection = useMediaQuery(FAI_COMPARATOR_SELECTION_BREAKPOINT)
        ? FAI_COMPARATOR_MOBILE_LIMIT
        : FAI_COMPARATOR_DESKTOP_LIMIT;

    useEffect(() => {
        const storage = getLocalDataIfNotExpired<ProviderSummary>(ELIGIBILITY_STORAGE_KEY.SUMMARY);
        setProviderSummary(storage);

        if (storage) {
            setButtonLabel('Voir les offres éligibles');
            setEligibilitySentence(
                storage.eligibleOffers?.some((offer) => offer.includes('ftth'))
                    ? 'Vous êtes éligible à la Fibre au\u00A0: '
                    : 'Vous êtes éligible à nos offres au\u00A0: ',
            );
        }

        // Position omnikit widget just before the sticky bar
        window.omnikit = { insertBeforeId: 'sticky-bar' };
    }, []);

    useEffect(() => {
        const handleSelectionChange = (event: Event) => {
            const { detail } = event as CustomEvent<FaiComparatorSelectionEventDetail>;
            setSelectedOffers(detail.selectedOffers);
        };

        window.addEventListener(CUSTOM_EVENTS.FAI_COMPARATOR_SELECTION, handleSelectionChange as EventListener);
        setSelectedOffers(getStoredFaiComparatorSelection());

        return () => {
            window.removeEventListener(CUSTOM_EVENTS.FAI_COMPARATOR_SELECTION, handleSelectionChange as EventListener);
        };
    }, []);

    const selectedOffersCount = selectedOffers.length;

    const comparatorUrl = useMemo(() => {
        if (!selectedOffersCount) {
            return paths.offersComparator;
        }

        const params = new URLSearchParams();

        const ranges = selectedOffers.map((offer) => `${offer.rangeNg}:${offer.commercialSeries}`);

        if (ranges.length) {
            params.set('ranges', ranges.join(','));
        }

        return `${paths.offersComparator}?${params.toString()}`;
    }, [selectedOffers, selectedOffersCount]);

    return (
        <Box className='is-radiusless is-sticky-bottom has-text-centered is-block is-marginless' id='sticky-bar'>
            {providerSummary && (
                <Text>
                    <b>{eligibilitySentence}</b>
                    {providerSummary.address.inline}
                </Text>
            )}
            <View flexable justify={Justify.CENTER} align={Align.CENTER} className='is-flex-wrap-nowrap buttons'>
                {selectedOffersCount > 0 && (
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        onClick={() => sendComparatorButtonTracking(selectedOffers)}
                        href={comparatorUrl}
                    >
                        {`Comparer ces box (${Math.min(maxComparatorSelection, selectedOffersCount)}/${maxComparatorSelection})`}
                    </Button>
                )}
                <Button
                    variant={ButtonVariant.PRIMARY}
                    href={`${paths.eligibilityTest}#${activeTab}`}
                    data-cy='home-elig-button'
                >
                    {buttonLabel}
                </Button>
            </View>
        </Box>
    );
}
